import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  TablePagination,
  Grid,
  Avatar,
} from '@mui/material';
import {
  Refresh,
  Search,
  AccountBalance,
  Person,
  CalendarToday,
  ArrowForward,
  CheckCircle,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import LimitService from '../../services/limitService';
import useRoleColors from '../../hooks/useRoleColors';

const PendingLimitRequests = ({ user }) => {
  const theme = useTheme();
  const { accent, accentGradient, accentGradientHover, softBg, softBgHover, contrast } = useRoleColors();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Fetch pending limit requests
  const fetchRequests = async () => {
    setLoading(true);
    setError('');

    try {
      const result = await LimitService.getPendingRequests();
      if (result.success) {
        setRequests(result.data || []);
      } else {
        setError(result.message || 'Failed to fetch pending limit requests');
      }
    } catch (err) {
      console.error('Error fetching pending limit requests:', err);
      setError('Server error occurred');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  // Filter requests based on search term
  const filteredRequests = requests.filter(request => {
    const searchLower = searchTerm.toLowerCase();
    return (
      request.userName?.toLowerCase().includes(searchLower) ||
      request.userId?.toString().includes(searchLower) ||
      request.currency?.toLowerCase().includes(searchLower)
    );
  });

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US');
  };

  // Handle define limit
  const handleDefineLimit = (request) => {
    navigate(`/admin/limits/define?userId=${request.userId}`);
  };

  // Paginated requests
  const paginatedRequests = filteredRequests.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box sx={{ 
      minHeight: '100vh',
      py: 4,
      px: { xs: 2, sm: 3 }
    }}>
      <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
        
        {/* Main Card */}
        <Card elevation={24} sx={{ 
          borderRadius: 4,
          overflow: 'hidden'
        }}>
          
          {/* Header Banner */}
          <Box sx={{ 
            background: accentGradient,
            p: 3,
            color: 'white'
          }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <Avatar sx={{ 
                  width: 64, 
                  height: 64,
                  background: 'rgba(255,255,255,0.25)',
                  border: '3px solid rgba(255,255,255,0.3)'
                }}>
                  <AccountBalance sx={{ fontSize: 32 }} />
                </Avatar>
              </Grid>
              <Grid item xs>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Pending Limit Requests
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Review and approve pending limit requests from users
                </Typography>
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <Refresh />}
                  onClick={fetchRequests}
                  disabled={loading}
                  sx={{
                    background: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    '&:hover': {
                      background: 'rgba(255,255,255,0.3)',
                    }
                  }}
                >
                  {loading ? 'Loading...' : 'Refresh'}
                </Button>
              </Grid>
            </Grid>
          </Box>

          {/* Content */}
          <CardContent sx={{ p: 4 }}>

            {/* Search and Filter */}
            <Box sx={{ mb: 3, p: 3, backgroundColor: 'grey.50', borderRadius: 3 }}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Search by user name, ID, or currency..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search color="primary" />
                    </InputAdornment>
                  )
                }}
              />
            </Box>

            {/* Error Alert */}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            {/* Requests Table */}
            <Box sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>User ID</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>User Name</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Currency</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Request Date</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'center' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 8 }}>
                      <CircularProgress size={40} sx={{ color: accent }} />
                      <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                        Loading requests...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : filteredRequests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 8 }}>
                      <Box sx={{ 
                        display: 'inline-flex', 
                        p: 3, 
                        borderRadius: 3, 
                        backgroundColor: 'grey.100',
                        mb: 2
                      }}>
                        <AccountBalance sx={{ fontSize: 48, color: 'grey.400' }} />
                      </Box>
                      <Typography variant="h6" sx={{ mb: 1, color: 'text.primary' }}>
                        No Limit Requests
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {searchTerm ? 'No requests match your search criteria.' : 'There are no limit requests at the moment.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedRequests.map((request) => (
                    <TableRow 
                      key={request.id} 
                      hover
                      sx={{
                        '&:hover': {
                          backgroundColor: softBg,
                        }
                      }}
                    >
                      <TableCell>
                        <Chip 
                          label={`#${request.userId}`} 
                          size="small" 
                          sx={{ 
                            fontWeight: 600, 
                            backgroundColor: softBg,
                            color: accent,
                            borderRadius: 3
                          }} 
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Person sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }} />
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                            {request.userName || 'N/A'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={request.currency || 'EUR'}
                          size="small"
                          color="primary"
                          sx={{ 
                            fontWeight: 600,
                            borderRadius: 3
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <CalendarToday sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }} />
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(request.createdOn)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label="Pending"
                          color="warning"
                          size="small"
                          sx={{ 
                            fontWeight: 600,
                            borderRadius: 3,
                            color: 'white'
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Tooltip title="Define Limit" arrow>
                          <IconButton 
                            size="small"
                            onClick={() => handleDefineLimit(request)}
                            sx={{
                              color: accent,
                              backgroundColor: softBg,
                              '&:hover': {
                                background: accentGradient,
                                color: contrast,
                                transform: 'scale(1.1)'
                              },
                              transition: 'all 0.2s'
                            }}
                          >
                            <ArrowForward fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Pagination */}
          {!loading && filteredRequests.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={filteredRequests.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Rows per page:"
              labelDisplayedRows={({ from, to, count }) => 
                `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
              }
              sx={{
                borderTop: '1px solid',
                borderColor: 'divider',
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  fontWeight: 500,
                  color: 'text.secondary'
                }
              }              }
            />
            )}
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default PendingLimitRequests;

